import { createBrowserRouter, Outlet, RouteObject, RouterProvider } from 'react-router-dom';
import { BpAuthProvider } from './BpAuthProvider';
import { LoginPage } from './pages/Login/LoginPage';
import { App } from './App';
import { Home } from './pages/Home/Home';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { PageNotFoundPage } from './pages/PageNotFound/PageNotFoundPage';
import * as Sentry from '@sentry/react';
import { SignupPage } from './pages/Signup/SignupPage';
import { JoinPage } from 'pages/Join/JoinPage';
import { CoursesPage } from './pages/Courses/CoursesPage';
import { CoursePage } from './pages/Courses/CoursePage';
import { MediaLibraryPage } from './pages/MediaLibrary/MediaLibraryPage';
import { MaterialArchivePage } from './pages/MaterialArchive/MaterialArchivePage';
import { TemplatesPage } from './pages/Templates/TemplatesPage';
import { AccountsPage } from './pages/Accounts/AccountsPage';
import { MessagesPage } from './pages/Messages/MessagesPage';
import { MeetingsPage } from './pages/Meetings/MeetingsPage';
import { InstitutionPage } from './pages/Institution/InstitutionPage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { SettingsOverviewSubpage } from './pages/Settings/subpages/SettingsOverviewSubpage';
import { SettingsClassbookSubpage } from './pages/Settings/subpages/SettingsClassbookSubpage';
import { AdminPage } from './pages/Admin/AdminPage';
import { InstitutionAdminSubpage } from './pages/Admin/subpages/InstitutionAdminSubpage';
import { OrganizationsSubpage } from './pages/Admin/subpages/OrganizationsSubpage';
import { CourseOverviewSubpage } from './pages/Courses/subpages/CourseOverviewSubpage';
import { CourseLessonsSubpage } from './pages/Courses/subpages/CourseLessonsSubpage';
import { BpSubpage } from './components/BpSubpage/BpSubpage';
import { CourseAssignmentsSubpage } from './pages/Courses/subpages/CourseAssignmentsSubpage';
import { CourseAssignmentSubpage } from './pages/Courses/subpages/CourseAssignmentSubpage';
import { Submission } from './components/Submission/Submission';
import { PublicSubmission } from './components/Submission/PublicSubmission';
import { CourseWorkMaterialsSubpage } from './pages/Courses/subpages/CourseWorkMaterialsSubpage';
import { CourseAppointmentsSubpage } from './pages/Courses/subpages/CourseAppointmentsSubpage';
import { CoursePersonsSubpage } from './pages/Courses/subpages/CoursePersonsSubpage';
import { CourseAdminSubpage } from 'pages/Courses/subpages/CourseAdminSubpage';
import { ClassbookPage } from 'pages/Classbook/ClassbookPage';
import { GroupPersonsSubpage } from './pages/Groups/subpages/GroupPersonsSubpage';
import { GroupAppointmentsSubpage } from 'pages/Groups/subpages/GroupAppointmentsSubpage';
import { GroupsPage } from 'pages/Groups/GroupsPage';
import { GroupOverviewSubpage } from './pages/Groups/subpages/GroupOverviewSubpage';
import { GroupPage } from './pages/Groups/GroupPage';
import { GroupWorkMaterialsSubpage } from 'pages/Groups/subpages/GroupWorkMaterialsSubpage';
import { InstitutionSchoolyearsSubpage } from './pages/Institution/subpages/InstitutionSchoolyearsSubpage';
import { InstitutionOverviewSubpage } from './pages/Institution/subpages/InstitutionOverviewSubpage';
import { InstitutionPersonsSubpage } from './pages/Institution/subpages/InstitutionPersonsSubpage';
import { InstitutionGroupsSubpage } from './pages/Institution/subpages/InstitutionGroupsSubpage';
import { InstitutionCoursesSubpage } from './pages/Institution/subpages/InstitutionCoursesSubpage';
import { InstitutionSubjectsSubpage } from './pages/Institution/subpages/InstitutionSubjectsSubpage';
import { SettingsCollaborationCoursesSubpage } from './pages/Settings/subpages/SettingsCollaborationCoursesSubpage';
import { SettingsCollaborationsSubpage } from './pages/Settings/subpages/SettingsCollaborationsSubpage';
import { SettingsCollaborationGroupsSubpage } from './pages/Settings/subpages/SettingsCollaborationGroupsSubpage';
import { ProfilePage } from './pages/Profile/ProfilePage';
import { AboutPage } from 'pages/About/AboutPage';
import { RunningBBBMeetingsSubpage } from 'pages/Meetings/subpages/RunningBBBMeetingsSubpage';
import { RunningZoomMeetingsSubpage } from 'pages/Meetings/subpages/RunningZoomMeetingsSubpage';
import { SettingsProcuratSubpage } from './pages/Settings/subpages/SettingsProcuratSubpage';
import { InstitutionClassesSubpage } from 'pages/Institution/subpages/InstitutionClassesSubpage';
import { ProcuratGroups } from './components/Procurat/ProcuratGroups';
import { ProcuratClasses } from './components/Procurat/ProcuratClasses';
import { ProcuratDepartments } from './components/Procurat/ProcuratDepartments';
import { ClassbookCoursesSubpage } from 'pages/Classbook/subpages/ClassbookCoursesSubpage';
import { ClassbookClassesSubpage } from 'pages/Classbook/subpages/ClassbookClassesSubpage';
import { ClassbookAbsencesSubpage } from 'pages/Classbook/subpages/ClassbookAbsencesSubpage';
import { ClassbookAdministrationSubpage } from 'pages/Classbook/subpages/ClassbookAdministrationSubpage';
import { ThreemaSubpage } from 'pages/Settings/subpages/ThreemaSubpage';

let wrappedBrowserRouter;
if (import.meta.env.VITE_APP_SENTRY_DSN !== 'false') {
  wrappedBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
} else {
  wrappedBrowserRouter = createBrowserRouter;
}

const router = wrappedBrowserRouter(
  [
    {
      path: '/login',
      element: (
        <BpAuthProvider>
          <LoginPage />
        </BpAuthProvider>
      ),
    },
    {
      path: '/signup/:invitationToken',
      element: (
        <BpAuthProvider>
          <SignupPage />
        </BpAuthProvider>
      ),
    },
    {
      path: '/join/:conference',
      element: (
        <BpAuthProvider>
          <JoinPage />
        </BpAuthProvider>
      ),
    },
    {
      path: '/',
      Component: App,
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: <Home />,
        },

        {
          path: 'courses',
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <CoursesPage />,
            },
            {
              path: ':courseUuid',
              element: <CoursePage />,
              children: [
                {
                  index: true,
                  id: 'course-overview',
                  element: <CourseOverviewSubpage />,
                },
                {
                  path: 'lessons',
                  element: <CourseLessonsSubpage />,
                  children: [
                    {
                      path: ':teachingUnitUuid',
                      element: <CourseLessonsSubpage />,
                    },
                  ],
                },

                {
                  path: 'assignments',
                  children: [
                    {
                      index: true,
                      element: <CourseAssignmentsSubpage />,
                    },
                    {
                      path: ':assignmentUuid',
                      children: [
                        {
                          index: true,
                          element: <CourseAssignmentSubpage />,
                        },
                        {
                          path: 'submissions/:submissionUuid',
                          element: <Submission />,
                        },
                        {
                          path: 'submissions/public/:submissionUuid',
                          element: <PublicSubmission />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'workmaterials',
                  element: <Outlet />,
                  children: [
                    { index: true, element: <CourseWorkMaterialsSubpage variant={'table'} /> },
                    {
                      path: ':uuid',
                      element: <CourseWorkMaterialsSubpage variant={'view'} />,
                    },
                    {
                      path: 'add',
                      element: <CourseWorkMaterialsSubpage variant={'add'} />,
                    },
                    {
                      path: 'edit/:uuid',
                      element: <CourseWorkMaterialsSubpage variant={'edit'} />,
                    },
                  ],
                },
                {
                  path: 'appointments',
                  element: <CourseAppointmentsSubpage />,
                  children: [
                    {
                      path: ':add',
                      element: <CourseAppointmentsSubpage />,
                    },
                    {
                      path: ':edit/:uuid',
                      element: <CourseAppointmentsSubpage />,
                    },
                  ],
                },
                {
                  path: 'persons',
                  element: <CoursePersonsSubpage />,
                },
                {
                  path: 'cloud',
                  element: (
                    <BpSubpage>
                      <></>
                    </BpSubpage>
                  ),
                },
                {
                  path: 'admin',
                  element: <CourseAdminSubpage />,
                },
              ],
            },
          ],
        },

        {
          path: 'groups',
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <GroupsPage />,
            },
            {
              path: ':groupUuid',
              element: <GroupPage />,
              children: [
                {
                  index: true,
                  id: 'group-overview',
                  element: <GroupOverviewSubpage />,
                },
                {
                  path: 'workmaterials',
                  element: <Outlet />,
                  children: [
                    {
                      index: true,
                      element: <GroupWorkMaterialsSubpage variant={'table'} />,
                    },
                    {
                      path: 'add',
                      element: <GroupWorkMaterialsSubpage variant={'add'} />,
                    },
                    {
                      path: ':uuid/edit',
                      element: <GroupWorkMaterialsSubpage variant={'edit'} />,
                    },
                    {
                      path: ':uuid',
                      element: <GroupWorkMaterialsSubpage variant={'detail'} />,
                    },
                  ],
                },
                {
                  path: 'appointments',
                  element: <GroupAppointmentsSubpage />,
                  children: [
                    {
                      path: ':add',
                      element: <GroupAppointmentsSubpage />,
                    },
                    {
                      path: ':edit/:uuid',
                      element: <GroupAppointmentsSubpage />,
                    },
                  ],
                },
                {
                  path: 'persons',
                  element: <GroupPersonsSubpage />,
                },
              ],
            },
          ],
        },

        {
          path: 'classbook/:dateParam?',
          element: <ClassbookPage />,
          children: [
            {
              index: true,
              element: <ClassbookCoursesSubpage />,
            },
            {
              path: 'classes',
              element: <ClassbookClassesSubpage />,
            },
            {
              path: 'absences',
              element: <ClassbookAbsencesSubpage />,
            },
            {
              path: 'administration',
              element: <ClassbookAdministrationSubpage />,
            },
          ],
        },

        {
          path: 'mediaLibrary',
          element: <MediaLibraryPage />,
        },

        {
          path: 'materialArchive',
          element: <MaterialArchivePage />,
        },

        {
          path: 'templates',
          element: <TemplatesPage />,
        },

        {
          path: 'accounts',
          element: <AccountsPage />,
        },

        {
          path: 'messages',
          element: <MessagesPage />,
          children: [
            {
              path: ':roomId',
              element: <MessagesPage />,
            },
          ],
        },

        {
          path: 'meetings',
          element: <Outlet />,
          children: [
            {
              element: <MeetingsPage />,
              children: [
                {
                  index: true,
                  element: <RunningBBBMeetingsSubpage />,
                },
                {
                  path: 'zoom',
                  element: <RunningZoomMeetingsSubpage />,
                },
              ],
            },
          ],
        },

        {
          path: 'institution',
          element: <InstitutionPage />,
          children: [
            {
              index: true,
              id: 'institution-overview',
              element: <InstitutionOverviewSubpage />,
            },

            {
              path: 'persons',
              element: <InstitutionPersonsSubpage />,
            },
            {
              path: 'groups',
              element: <InstitutionGroupsSubpage />,
            },
            {
              path: 'courses',
              element: <InstitutionCoursesSubpage />,
            },
            {
              path: 'classes',
              element: <InstitutionClassesSubpage />,
            },
            {
              path: 'subjects',
              element: <InstitutionSubjectsSubpage />,
            },
            {
              path: 'schoolyears',
              element: <InstitutionSchoolyearsSubpage />,
            },
          ],
        },

        {
          path: 'settings',
          element: <SettingsPage />,
          children: [
            {
              index: true,
              id: 'settings-overview',
              element: <SettingsOverviewSubpage />,
            },
            {
              path: 'classbook',
              element: <SettingsClassbookSubpage />,
            },
            {
              path: 'collaborations',
              element: <SettingsCollaborationsSubpage />,
            },
            {
              path: 'collaboration-groups',
              element: <SettingsCollaborationGroupsSubpage />,
            },
            {
              path: 'collaboration-courses',
              element: <SettingsCollaborationCoursesSubpage />,
            },
            {
              path: 'procurat',
              element: <SettingsProcuratSubpage />,
              children: [
                {
                  id: 'departments',
                  index: true,
                  element: <ProcuratDepartments />,
                },
                {
                  path: 'classes',
                  id: 'classes',
                  element: <ProcuratClasses />,
                },
                {
                  id: 'persongroups',
                  path: 'persongroups',
                  element: <ProcuratGroups />,
                },
              ],
            },
            {
              path: 'threema-work',
              element: <ThreemaSubpage />,
            },
          ],
        },
        {
          path: 'profile',
          element: <ProfilePage />,
        },
        {
          path: 'admin',
          element: <AdminPage />,
          children: [
            {
              path: 'admin',
              element: <InstitutionAdminSubpage />,
            },
            {
              path: 'organizations',
              element: <OrganizationsSubpage />,
            },
          ],
        },
        {
          path: 'app-about',
          element: <AboutPage />,
        },
      ],
    },
    {
      path: 'about',
      element: <AboutPage />,
    },
    {
      path: '*',
      element: <PageNotFoundPage />,
    },
  ] as RouteObject[],
  {
    basename: import.meta.env.VITE_APP_BASEPATH,
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_relativeSplatPath: true,
    },
  },
);

export const Router = () => {
  return <RouterProvider router={router} />;
};
